'use client';

import { useEffect, useState, useMemo, forwardRef, useImperativeHandle, useCallback } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import TransferListTable from '../../../../components/TransferListTable';
import { updateCompanyForRole, createCompanyForRole } from '../../../../stores/roleStore';
import { useCompanyList } from '../../../../stores/companyStore';
import { useLanguage } from '../../../../utils/i18n';

const CompanyForRoleDialog = forwardRef(function CompanyForRoleDialog({ roleName, dataCompanyList, onDirtyChange, onSaved }, ref) {
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const { t } = useLanguage();

  const { companyOptions, isLoading: isCompanyLoading } = useCompanyList();

  const roleCompanyObj = useMemo(() => {
    if (Array.isArray(dataCompanyList) && dataCompanyList.length > 0) {
      return dataCompanyList[0];
    }
    return dataCompanyList || null;
  }, [dataCompanyList]);

  useEffect(() => {
    if (isCompanyLoading) {
      setLoading(true);
      return;
    }

    try {
      const assignedIds = new Set((roleCompanyObj?.company_id || roleCompanyObj?.company || []).map((id) => String(id)));

      const allCompanies = (companyOptions || []).map((opt) => ({
        id: String(opt.value ?? opt.id),
        name: String(opt.label ?? opt.name),
        code: opt._raw?.code ?? opt._raw?.company_code ?? '-',
        business_type: opt._raw?.join?.business_type_name ?? opt._raw?.business_type ?? '-',
        phone: opt._raw?.phone1 ?? opt._raw?.phone ?? opt._raw?.company_phone ?? '-'
      }));

      const assigned = [];
      const available = [];

      allCompanies.forEach((c) => {
        if (assignedIds.has(c.id)) {
          assigned.push(c);
        } else {
          available.push(c);
        }
      });

      const sortByName = (a, b) => a.name.localeCompare(b.name);
      available.sort(sortByName);
      assigned.sort(sortByName);

      setLeftItems(available);
      setRightItems(assigned);

      setDirty(false);
      if (typeof onDirtyChange === 'function') onDirtyChange(false);
    } catch (err) {
      console.error('Error mapping companies:', err);
    } finally {
      setLoading(false);
    }
  }, [roleCompanyObj, companyOptions, isCompanyLoading, onDirtyChange]);

  const columns = useMemo(
    () => [
      { field: 'name', label: t('masterCompany.companyName', 'Company Name') },
      { field: 'code', label: t('masterCompany.companyCode', 'Company Code'), render: (it) => it.code ?? '-' },
      { field: 'business_type', label: t('masterCompany.businessType', 'Business Type'), render: (it) => it.business_type ?? '-' },
      { field: 'phone', label: t('masterCompany.phone', 'Phone'), render: (it) => it.phone ?? '-' }
    ],
    [t]
  );

  const handleChange = useCallback(
    ({ left = [], right = [] }) => {
      const leftObjs = (left || []).map((id) => {
        const found = leftItems.find((c) => String(c.id) === String(id)) || rightItems.find((c) => String(c.id) === String(id));
        return found ? { ...found } : { id: String(id), name: String(id) };
      });

      const rightObjs = (right || []).map((id) => {
        const found = rightItems.find((c) => String(c.id) === String(id)) || leftItems.find((c) => String(c.id) === String(id));
        return found ? { ...found } : { id: String(id), name: String(id) };
      });

      const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');
      setLeftItems(leftObjs.sort(sortByName));
      setRightItems(rightObjs.sort(sortByName));

      setDirty(true);
      if (typeof onDirtyChange === 'function') onDirtyChange(true);
    },
    [leftItems, rightItems, onDirtyChange]
  );

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const newCompanyIds = (rightItems || []).map((c) => Number(c.id));

      const roleId = roleCompanyObj?.id || 0;

      const payload = {
        id: roleId,
        company_id: newCompanyIds,
        role_name: roleName
      };
      const payloadUpdate = {
        id: roleId,
        company_id: newCompanyIds,
        role_name: roleName
      };
      if (roleCompanyObj && roleCompanyObj.id) {
        await updateCompanyForRole(roleCompanyObj.id, payloadUpdate);
      } else {
        await createCompanyForRole(payload);
      }

      setDirty(false);
      if (typeof onDirtyChange === 'function') onDirtyChange(false);

      if (typeof onSaved === 'function') onSaved({ roleId: roleId, assigned: newCompanyIds });

      return { success: true, assigned: newCompanyIds };
    } catch (err) {
      console.error('Failed saving role companies:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [rightItems, roleCompanyObj, roleName, onDirtyChange, onSaved]);
  useImperativeHandle(
    ref,
    () => ({
      save,
      isDirty: () => dirty,
      getAssignedIds: () => (rightItems || []).map((c) => String(c.id ?? c))
    }),
    [save, dirty, rightItems]
  );

  if (!roleCompanyObj) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1">{t('masterRole.dataCompanyNotAvailable', 'Data Role Company is not available.')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {loading || saving ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TransferListTable
          leftItems={leftItems}
          rightItems={rightItems}
          onChange={handleChange}
          titleLeft={t('masterRole.availableCompanies', 'Available Companies')}
          titleRight={t('masterRole.chosenCompanies', 'Chosen Companies')}
          itemKey="id"
          itemLabel="name"
          showSearch
          columns={columns}
        />
      )}
    </Box>
  );
});

export default CompanyForRoleDialog;
